import type { TouchEvent } from 'react';
import { useRef } from 'react';

interface PinchZoomOptions {
  setValue: (updater: (value: number) => number) => void;
  min: number;
  max: number;
  sensitivity?: number;
}

function touchDistance(event: TouchEvent<HTMLElement>) {
  const first = event.touches.item(0);
  const second = event.touches.item(1);
  if (!first || !second) return null;
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

export function usePinchZoom({ setValue, min, max, sensitivity = 0.16 }: PinchZoomOptions) {
  const lastDistanceRef = useRef<number | null>(null);

  return {
    onTouchStart(event: TouchEvent<HTMLElement>) {
      if (event.touches.length === 2) {
        lastDistanceRef.current = touchDistance(event);
      }
    },
    onTouchMove(event: TouchEvent<HTMLElement>) {
      if (event.touches.length !== 2) return;
      const nextDistance = touchDistance(event);
      const previousDistance = lastDistanceRef.current;
      if (nextDistance == null || previousDistance == null) return;

      event.preventDefault();
      const delta = nextDistance - previousDistance;
      if (Math.abs(delta) < 2) return;
      setValue(value => Math.max(min, Math.min(max, value + delta * sensitivity)));
      lastDistanceRef.current = nextDistance;
    },
    onTouchEnd(event: TouchEvent<HTMLElement>) {
      if (event.touches.length < 2) {
        lastDistanceRef.current = null;
      }
    },
  };
}
